import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Input,
  Text,
  Spacer,
  Stack,
  FormRow
} from '@efast_public/fjd-component-library';
import { useNotification } from '../shared/utils/notification/useNotification';
import { GovosApiRoutes, govosClient } from '../api/govos-api';
import { RouteKey } from './AppRoutes';

interface LoginCredentials {
  login: string;
  password: string;
}

interface LoginFormData {
  email: string;
  password: string;
}

export default function LoginPage() {
  const { t } = useTranslation();
  const { notifyUserFromRequest } = useNotification();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors, isValid }
  } = useForm<LoginFormData>({
    mode: 'onChange',
    reValidateMode: 'onChange'
  });

  const loginMutation = useMutation({
    mutationFn: async (credentials: LoginCredentials) =>
      notifyUserFromRequest(
        () => govosClient.post(GovosApiRoutes.login(), credentials),
        {
          loadingMessage: t('LoginPage.loadingMessage'),
          successMessage: t('LoginPage.successMessage'),
          errorMessage: t('LoginPage.errorMessage')
        }
      ),
    onSuccess: (data) => {
      if (data.result === 'success') {
        queryClient
          .refetchQueries({ queryKey: [GovosApiRoutes.user()] })
          .then(() => {
            navigate(RouteKey.DASHBOARD);
          })
          .catch((error) => {
            console.error(error);
          });
        queryClient
          .refetchQueries({ queryKey: [GovosApiRoutes.products()] })
          .then(() => {
            navigate(RouteKey.DASHBOARD);
          })
          .catch((error) => {
            console.error(error);
          });
      }
    }
  });

  const onSubmit = (data: LoginFormData) => {
    loginMutation.mutate({ login: data.email, password: data.password });
  };

  return (
    <div style={{ maxWidth: '680px', margin: '0 auto', padding: '40px 20px' }}>
      <Stack direction="column" spacing="l">
        <Stack>
          <Text size="xxl" strong style={{ marginBottom: '8px' }}>
            {t('LoginPage.title')}
          </Text>

          <Text size="m">{t('LoginPage.subtitle')}</Text>
        </Stack>

        <Spacer size="l" />

        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack direction="column" spacing="m">
            <div>
              <FormRow
                label={t('LoginPage.emailLabel')}
                validationMessage={errors.email?.message}
                variant="error"
              >
                <Input
                  data-testid="login-input"
                  type="text"
                  placeholder=""
                  style={{ width: '100%', maxWidth: '350px' }}
                  {...register('email', {
                    required: t('LoginPage.emailRequired'),
                    minLength: {
                      value: 1,
                      message: t('LoginPage.passwordMinLength')
                    }
                  })}
                />
              </FormRow>
            </div>

            <div>
              <FormRow
                label={t('LoginPage.passwordLabel')}
                validationMessage={errors.password?.message}
                variant="error"
              >
                <Input
                  data-testid="password-input"
                  type="password"
                  placeholder=""
                  style={{ width: '100%', maxWidth: '350px' }}
                  {...register('password', {
                    required: t('LoginPage.passwordRequired'),
                    minLength: {
                      value: 8,
                      message: t('LoginPage.passwordMinLength')
                    }
                  })}
                />
              </FormRow>
            </div>

            <div
              style={{
                display: 'flex',
                gap: '16px',
                alignItems: 'center',
                marginTop: '8px'
              }}
            >
              <Button
                data-testid="password-reset"
                type="button"
                variant="primary-text"
                onClick={() => navigate(RouteKey.RESET_PASSWORD)}
              >
                {t('LoginPage.forgotPassword')}
              </Button>
              <Button
                data-testid="submit-button"
                type="submit"
                variant="primary"
                disabled={loginMutation.isPending || !isValid}
              >
                {loginMutation.isPending
                  ? t('LoginPage.submitting')
                  : t('LoginPage.submit')}
              </Button>
            </div>
          </Stack>
        </form>
      </Stack>
    </div>
  );
}
